import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import {
  postChat,
  getAiHistory,
  getFilesList,
  createCheckpoint,
  updateAiConfig,
  ChatMessage,
} from '../helpers/chatApiClient';
import { useAuth } from './useAuth';
import { extractFilesFromMarkdown } from './extractFilesFromMarkdown';
import { scanAllImports, findMissingImports, generateStub } from './stubGenerator';
import { getShadcnBundledFiles } from './shadcnBundle';
import { parseStreamingPlan } from './aiPlanningParser';
import { createFile as createFileEndpoint } from '../endpoints/files/create_POST.schema';
import { updateFile as updateFileEndpoint } from '../endpoints/files/update_POST.schema';
import { toast } from 'sonner';

// Strip injected skill context from message content so it's invisible in the UI
const SKILL_INJECTION_RE =
  /\n\n---\n\*\*Active Agent Skills — Apply these skills to your response:\*\*[\s\S]*?---$/;

function stripSkillInjection(content: string): string {
  return content.replace(SKILL_INJECTION_RE, '');
}

export const useAiChatHistory = (sessionId: string) => {
  return useQuery({
    queryKey: ['ai', 'history', sessionId],
    queryFn: () => getAiHistory({ sessionId, limit: 20 }),
    enabled: !!sessionId,
    select: (data) => ({
      ...data,
      messages: data.messages.map((msg) =>
        msg.role === 'user' && typeof msg.content === 'string'
          ? { ...msg, content: stripSkillInjection(msg.content) }
          : msg,
      ),
    }),
    retry: (failureCount, error) => {
      // Don't retry on authentication errors (401)
      if (error instanceof Error && error.message === 'Not authenticated') {
        return false;
      }
      if (error instanceof Error && /D1_ERROR|memory limit|Network connection lost/i.test(error.message)) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchInterval: (query) => {
      // Pause polling when tab is hidden or when query is in error state.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
      if (query.state.error) return false;
      return 30000;
    }, // Poll every 30s - WebSocket handles real-time updates
    refetchOnWindowFocus: false,
  });
};

export const useUpdateAiConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof updateAiConfig>[0]) => updateAiConfig(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sessions', 'detail', data.session.id] });
    },
  });
};

export type StreamingMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  createdAt: Date;
  userDisplayName?: string;
  userAvatarUrl?: string | null;
};

export type ModelSelectionOptions = {
  useLocalLlm?: boolean;
  provider?: 'anthropic' | 'ollama_cloud' | 'ollama_local' | 'openai' | 'openrouter';
  model?: string;
  endpoint?: string;
  apiKey?: string;
};

type ApplySuggestionResult = {
  successCount: number;
  failCount: number;
  createdFiles: string[];
  updatedFiles: string[];
  skipped?: boolean;
  reason?: string;
};

const PREVIEW_BOOTSTRAP_FILES = new Set([
  'index.html',
  'tsconfig.node.json',
  'src/main.tsx',
  'src/index.css',
  'src/App.tsx',
  'src/lib/utils.ts',
]);

function normalizeSessionPath(filePath: string, fileName: string): string {
  const dir = String(filePath || '/')
    .replace(/\/+$/, '')
    .replace(/^\/*/, '');
  const name = String(fileName || '').replace(/^\/+/, '');
  return `${dir ? `${dir}/` : ''}${name}`.replace(/^\/+/, '');
}

function isBootstrapRecoveryResponse(content: string): boolean {
  const value = String(content || '').toLowerCase();
  if (!value) return false;
  return (
    value.includes('essential project files are missing') ||
    value.includes('missing from the virtual filesystem') ||
    value.includes('previous attempts were truncated') ||
    value.includes('create all six required files') ||
    value.includes('a vite-style react project requires complete entry files')
  );
}

function buildPreviewBootstrapFile(filename: string): { filename: string; code: string } | null {
  switch (filename) {
    case 'tsconfig.node.json':
      return {
        filename,
        code: `{
  "compilerOptions": {
    "composite": true,
    "module": "ESNext",
    "moduleResolution": "Bundler",
    "allowSyntheticDefaultImports": true
  },
  "include": ["vite.config.ts"]
}
`,
      };
    case 'index.html':
      return {
        filename,
        code: `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Tasking Preview</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>
`,
      };
    case 'src/index.css':
      return {
        filename,
        code: `:root {
  color-scheme: dark;
  font-family: Inter, system-ui, sans-serif;
  background: var(--background, #0b0f17);
  color: var(--foreground, #f5f8ff);
}

* {
  box-sizing: border-box;
}

html,
body,
#root {
  margin: 0;
  min-height: 100%;
}

body {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(249, 115, 22, 0.12), transparent 38%),
    var(--background, #0b0f17);
  color: var(--foreground, #f5f8ff);
}

button,
input,
textarea,
select {
  font: inherit;
}
`,
      };
    case 'src/main.tsx':
      return {
        filename,
        code: `import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
`,
      };
    case 'src/App.tsx':
      return {
        filename,
        code: `import { useState } from 'react';

export default function App() {
  const [count, setCount] = useState(0);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f0f1e, #1a1a3e)', color: '#e4e1e9', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <header style={{ padding: '1.5rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
          <span style={{ color: '#c0c1ff' }}>My</span> App
        </h1>
        <span style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'monospace' }}>v1.0.0</span>
      </header>

      <main style={{ maxWidth: '640px', margin: '0 auto', padding: '3rem 2rem' }}>
        <section style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '2.5rem', textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.03em' }}>
            Build Something <span style={{ color: '#c0c1ff' }}>Amazing</span>
          </h2>
          <p style={{ margin: '0 0 2rem', color: '#888', fontSize: '0.95rem', lineHeight: 1.6 }}>
            Start editing files in the sidebar. The live preview updates in real-time.
          </p>
          <button
            onClick={() => setCount(c => c + 1)}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '10px',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.1s',
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
            onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          >
            Clicked {count} time{count !== 1 ? 's' : ''}
          </button>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.5rem' }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888', marginBottom: '0.5rem' }}>Framework</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>React + Vite</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888', marginBottom: '0.5rem' }}>Styling</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>Tailwind v4</div>
          </div>
        </div>
      </main>
    </div>
  );
}
`,
      };
    case 'src/lib/utils.ts':
      return {
        filename,
        code: `export function cn(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}
`,
      };
    default:
      return null;
  }
}

function ensurePreviewBootstrapFiles(
  files: Array<{ filename: string; code: string }>,
  existingFiles: Array<any>,
): Array<{ filename: string; code: string }> {
  const existing = new Set<string>();
  for (const f of existingFiles || []) {
    existing.add(normalizeSessionPath(f.filePath || '/', f.fileName || ''));
  }
  for (const f of files) {
    existing.add(String(f.filename || '').replace(/^\/+/, ''));
  }

  const missing: Array<{ filename: string; code: string }> = [];
  for (const bootstrapFile of PREVIEW_BOOTSTRAP_FILES) {
    if (existing.has(bootstrapFile)) continue;
    const next = buildPreviewBootstrapFile(bootstrapFile);
    if (next) missing.push(next);
  }
  return missing;
}

export const useSendAiMessage = (
  sessionId: string,
  autoExecute: boolean = true,
  mode: 'generate' | 'chat' = 'generate',
  onCreateTask?: (stepText: string) => Promise<void>,
  autoLoop: boolean = false, // Auto loop mode: AI executes steps automatically
) => {
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const [streamingMessage, setStreamingMessage] = useState<StreamingMessage | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [lastGeneratedCode, setLastGeneratedCode] = useState<string | null>(null);
  const [activePersonas, setActivePersonas] = useState<string[]>([]);
  const emptyCodeRetryRef = useRef(false); // Guard against infinite retries when AI returns no code
  const sendMessageRef = useRef<Function | null>(null); // Ref for retry access
  const queuedMessagesRef = useRef<Array<{ message: string; fileContext: string[]; modelSelection?: any; attachment?: any; webSearchResults?: any; skills?: any }>>([]); // Messages queued while streaming

  // Define applySuggestion first so it can be used in sendMessage
  const applySuggestion = useCallback(
    async (code: string): Promise<ApplySuggestionResult> => {
      console.log('[applySuggestion] Starting, code length:', code?.length || 0);
      console.log('[applySuggestion] Code preview (first 1000 chars):', code?.substring(0, 1000));

      // Extract files from markdown/code blocks
      let files = extractFilesFromMarkdown(code);
      console.log(
        '[applySuggestion] Extracted files:',
        files.length,
        files.map((f) => ({ filename: f.filename, codeLength: f.code.length })),
      );

      // If no files found and code doesn't look like markdown, try wrapping it
      if (!files.length && !code.includes('```') && code.trim().length > 0) {
        console.log(
          '[applySuggestion] No markdown found, code might be plain text. This should not happen if filename was properly included.',
        );
        // This shouldn't happen if we're wrapping with filename, but just in case
        toast.error('No files found. Please ensure the code includes filename information.');
        return {
          successCount: 0,
          failCount: 0,
          createdFiles: [],
          updatedFiles: [],
          skipped: true,
          reason: 'no_files_found',
        };
      }

      if (!files.length) {
        console.warn('[applySuggestion] No files found in AI output.');
        console.warn('[applySuggestion] Full code content:', code);
        toast.error(
          'No files found in AI output. Make sure code blocks include filename=... in the info string.',
        );
        return {
          successCount: 0,
          failCount: 0,
          createdFiles: [],
          updatedFiles: [],
          skipped: true,
          reason: 'no_files_extracted',
        };
      }
      // Use sessionId from closure
      if (!sessionId) {
        toast.error('Session ID not found. Cannot create/update files.');
        return {
          successCount: 0,
          failCount: 1,
          createdFiles: [],
          updatedFiles: [],
          reason: 'missing_session_id',
        };
      }

      // Create an automatic checkpoint before making any overwrites (auto-backup)
      let preApplyCheckpointId: string | null = null;
      try {
        // Build descriptive checkpoint name with file info
        const fileNames = files.map((f) => f.filename.split('/').pop() || f.filename);
        const filesSummary =
          fileNames.length <= 3
            ? fileNames.join(', ')
            : `${fileNames.slice(0, 2).join(', ')} +${fileNames.length - 2} more`;
        const timeStr = new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        const ckName = `Backup before: ${filesSummary} (${timeStr})`;
        const ckDescription = `Auto backup before AI modifies: ${fileNames.join(', ')}`;

        console.log('[applySuggestion] Creating pre-apply checkpoint:', ckName);
        const ckRes = await createCheckpoint({
          sessionId,
          name: ckName,
          description: ckDescription,
          checkpointType: 'auto',
        });
        preApplyCheckpointId = ckRes?.checkpoint?.id ?? null;
        queryClient.invalidateQueries({ queryKey: ['checkpoints', 'list', sessionId] });
      } catch (ckErr) {
        console.warn(
          '[applySuggestion] Failed to create pre-apply checkpoint (continuing):',
          ckErr,
        );
      }

      // Get current file list for the session
      let fileList: any[] = [];
      try {
        const cached = queryClient.getQueryData(['files', 'list', sessionId]);
        if (
          cached &&
          typeof cached === 'object' &&
          'files' in cached &&
          Array.isArray((cached as any).files)
        ) {
          fileList = (cached as any).files;
        } else {
          // If not cached, fetch from server (use static import to avoid duplicate module instances)
          const result = await getFilesList({ sessionId });
          fileList = result.files;
        }
      } catch (e) {
        // fallback: allow create only
        fileList = [];
      }

      // ── Auto-stub missing imports BEFORE writing files ──
      // Scans all generated files for imports (relative + @/ alias) and generates
      // smart stubs for any that don't have a matching file.
      try {
        // Build the known file set: existing files + generated files + shadcn bundled files
        const knownFiles = new Set<string>();
        // Add existing files from DB
        for (const f of fileList) {
          const fullPath = (
            (f.filePath || '/').replace(/^\//, '').replace(/\/$/, '') +
            '/' +
            (f.fileName || '')
          ).replace(/^\//, '');
          knownFiles.add(fullPath.toLowerCase());
        }
        // Add files about to be generated
        for (const f of files) {
          knownFiles.add(f.filename.toLowerCase());
        }
        // Add shadcn bundled file paths
        for (const p of Object.keys(getShadcnBundledFiles())) {
          knownFiles.add(p.toLowerCase());
        }

        const allImports = scanAllImports(files);
        const missingImports = findMissingImports(allImports, knownFiles);

        if (missingImports.length > 0) {
          const stubbed: string[] = [];
          for (const imp of missingImports) {
            const stubCode = generateStub(imp);
            files.push({ filename: imp.suggestedFilePath, code: stubCode });
            stubbed.push(imp.suggestedFilePath.split('/').pop() || imp.suggestedFilePath);
            console.log(
              `[applySuggestion] Auto-stubbed missing import: ${imp.suggestedFilePath} (from ${imp.sourceFile})`,
            );
          }
          toast.warning(
            `${stubbed.length} missing file${stubbed.length > 1 ? 's' : ''} auto-stubbed: ${stubbed.slice(0, 3).join(', ')}${stubbed.length > 3 ? ` (+${stubbed.length - 3} more)` : ''}`,
            {
              description:
                'Placeholder files were created. The AI will fill them in on the next step.',
              duration: 6000,
            },
          );
        }
      } catch (stubErr) {
        console.warn('[applySuggestion] Import stub generation failed:', stubErr);
      }

      try {
        const bootstrapFiles = ensurePreviewBootstrapFiles(files, fileList);
        if (bootstrapFiles.length > 0) {
          files.push(...bootstrapFiles);
          toast.message(
            `Bootstrapped ${bootstrapFiles.length} missing preview file${bootstrapFiles.length > 1 ? 's' : ''}`,
            {
              description:
                'Added the core entry files once so the preview does not keep asking the AI to recreate the whole app.',
              duration: 5000,
            },
          );
        }
      } catch (bootstrapErr) {
        console.warn('[applySuggestion] Failed bootstrapping preview files:', bootstrapErr);
      }

      // Process ALL files - batch operations to ensure all are created
      const operations: Array<() => Promise<void>> = [];
      const results: Array<{
        success: boolean;
        filename: string;
        op?: 'create' | 'update';
        error?: string;
      }> = [];
      const bootstrapRecovery = isBootstrapRecoveryResponse(code);

      for (const { filename, code: fileContent } of files) {
        // Normalize filename: remove leading/trailing slashes and split into path and name
        const normalizedFilename = filename.replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes
        const pathParts = normalizedFilename.split('/');
        const fileName = pathParts.pop() || normalizedFilename; // Last part is the filename
        const filePath = pathParts.length > 0 ? '/' + pathParts.join('/') + '/' : '/'; // Directory path

        console.log('[applySuggestion] Processing file:', {
          filename,
          normalizedFilename,
          fileName,
          filePath,
        });

        // Match existing file by both filePath and fileName
        // Normalize both paths for comparison (handle trailing slashes, leading slashes)
        const existing = fileList.find((f) => {
          const fPath = String(f.filePath || '/').trim();
          const normalizedFPath = fPath === '/' ? '/' : fPath.endsWith('/') ? fPath : fPath + '/';
          const normalizedTargetPath =
            filePath === '/' ? '/' : filePath.endsWith('/') ? filePath : filePath + '/';
          const pathMatch = normalizedFPath === normalizedTargetPath;
          const nameMatch = String(f.fileName || '').trim() === fileName.trim();

          // Also try matching by full path (filePath + fileName) for better compatibility
          const fFullPath =
            (fPath === '/' ? '' : fPath.replace(/\/$/, '')) + '/' + String(f.fileName || '');
          const targetFullPath =
            (filePath === '/' ? '' : filePath.replace(/\/$/, '')) + '/' + fileName;
          const fullPathMatch =
            fFullPath === targetFullPath ||
            fFullPath.replace(/^\//, '') === targetFullPath.replace(/^\//, '') ||
            '/' + fFullPath.replace(/^\//, '') === targetFullPath ||
            fFullPath === '/' + targetFullPath.replace(/^\//, '');

          return (pathMatch && nameMatch) || fullPathMatch;
        });

        // Auto-overwrite behavior: if file exists, update it. If not found in cache, refresh list and retry before creating.
        if (existing) {
          operations.push(async () => {
            try {
              const normalizedTarget = normalizeSessionPath(filePath, fileName);
              const shouldProtectBootstrap =
                bootstrapRecovery &&
                PREVIEW_BOOTSTRAP_FILES.has(normalizedTarget) &&
                String(existing.content || '').trim().length > 0;
              if (shouldProtectBootstrap) {
                console.log('[applySuggestion] Skipping bootstrap overwrite during recovery:', normalizedTarget);
                results.push({ success: true, filename, op: 'update' });
                return;
              }
              console.log('[applySuggestion] Updating existing file:', existing.id, fileName);
              await updateFileEndpoint({
                id: existing.id,
                fileName: existing.fileName,
                content: fileContent,
              });
              results.push({ success: true, filename, op: 'update' });
            } catch (err: any) {
              console.error('[applySuggestion] Failed to update file:', err);
              results.push({
                success: false,
                filename,
                op: 'update',
                error: err?.message || String(err),
              });
            }
          });
        } else {
          // Try refreshing file list to avoid stale cache issues (race conditions)
          operations.push(async () => {
            try {
              const refreshed = await getFilesList({ sessionId });
              const freshList = refreshed.files || [];
              const newlyFound = freshList.find((f) => {
                const fFull =
                  ((f.filePath || '/') === '/' ? '' : (f.filePath || '').replace(/\/$/, '')) +
                  '/' +
                  (f.fileName || '');
                const targetFull =
                  (filePath === '/' ? '' : filePath.replace(/\/$/, '')) + '/' + fileName;
                return (
                  fFull === targetFull || fFull.replace(/^\//, '') === targetFull.replace(/^\//, '')
                );
              });

              if (newlyFound) {
                // File was created by another process; update instead
                console.log(
                  '[applySuggestion] Detected file after refresh, updating instead of creating:',
                  newlyFound.id,
                  filename,
                );
                await updateFileEndpoint({
                  id: newlyFound.id,
                  fileName: newlyFound.fileName,
                  content: fileContent,
                });
                results.push({ success: true, filename, op: 'update' });
                return;
              }

              console.log('[applySuggestion] Creating new file:', {
                sessionId,
                fileName,
                filePath,
                contentLength: fileContent?.length || 0,
              });
              try {
                await createFileEndpoint({
                  sessionId,
                  fileName: fileName,
                  filePath: filePath,
                  content: fileContent,
                });
                results.push({ success: true, filename, op: 'create' });
              } catch (createError: any) {
                console.error(
                  '[applySuggestion] Create file failed:',
                  createError?.message || createError,
                );
                throw createError;
              }
            } catch (createErr: any) {
              console.error(
                '[applySuggestion] Failed to create file; attempting fallback update if file exists:',
                createErr,
              );
              // If create failed due to 'already exists' or conflict, try to fetch file list and update
              try {
                const refreshed2 = await getFilesList({ sessionId });
                const freshList2 = refreshed2.files || [];
                const maybe = freshList2.find((f) => {
                  const fFull =
                    ((f.filePath || '/') === '/' ? '' : (f.filePath || '').replace(/\/$/, '')) +
                    '/' +
                    (f.fileName || '');
                  const targetFull =
                    (filePath === '/' ? '' : filePath.replace(/\/$/, '')) + '/' + fileName;
                  return (
                    fFull === targetFull ||
                    fFull.replace(/^\//, '') === targetFull.replace(/^\//, '')
                  );
                });
                if (maybe) {
                  console.log(
                    '[applySuggestion] Fallback: updating file found after create error:',
                    maybe.id,
                  );
                  await updateFileEndpoint({
                    id: maybe.id,
                    fileName: maybe.fileName,
                    content: fileContent,
                  });
                  results.push({ success: true, filename, op: 'update' });
                } else {
                  results.push({
                    success: false,
                    filename,
                    op: 'create',
                    error: createErr?.message || String(createErr),
                  });
                }
              } catch (fallbackErr: any) {
                console.error('[applySuggestion] Fallback update failed:', fallbackErr);
                results.push({
                  success: false,
                  filename,
                  op: 'create',
                  error: fallbackErr?.message || String(fallbackErr),
                });
              }
            }
          });
        }
      }

      // Execute all operations (sequential to avoid race conditions, but ensure all run)
      for (const op of operations) {
        await op();
      }

      // Invalidate file list cache once after all operations
      queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });

      // Show summary toast
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      // Build arrays of created/updated files for README and UI deltas
      const createdFiles = results
        .filter((r) => r.success && r.op === 'create')
        .map((r) => r.filename);
      const updatedFiles = results
        .filter((r) => r.success && r.op === 'update')
        .map((r) => r.filename);

      if (successCount > 0 && failCount === 0) {
        // Silent success — completion chime is sufficient
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(
          `Created/updated ${successCount} file${successCount > 1 ? 's' : ''}, but ${failCount} failed`,
        );
        results
          .filter((r) => !r.success)
          .forEach((r) => {
            toast.error(`Failed: ${r.filename} - ${r.error}`);
          });
      } else if (failCount > 0) {
        toast.error(`Failed to create/update ${failCount} file${failCount > 1 ? 's' : ''}`);
        results
          .filter((r) => !r.success)
          .forEach((r) => {
            toast.error(`Failed: ${r.filename} - ${r.error}`);
          });
      }

      // Dispatch an event so UI can show the auto-backup details and Undo action
      try {
        if (preApplyCheckpointId) {
          window.dispatchEvent(
            new CustomEvent('tasking.applyCheckpoint', {
              detail: { checkpointId: preApplyCheckpointId, createdFiles, updatedFiles },
            }),
          );
        }
      } catch (e) {
        console.warn('[applySuggestion] Failed to dispatch applyCheckpoint event', e);
      }

      // Ensure CHANGES.md is present or updated with a summary of these actions
      // (Uses CHANGES.md to track AI-generated changes, keeping README.md for project docs)
      // Only write CHANGES.md if at least one file was actually created or updated
      if (createdFiles.length === 0 && updatedFiles.length === 0) {
        // Nothing changed — skip CHANGES.md entirely to avoid noise
        return {
          successCount,
          failCount,
          createdFiles,
          updatedFiles,
        };
      }
      try {
        const summaryLines = [] as string[];
        summaryLines.push(`## Changes on ${new Date().toISOString()}`);
        if (createdFiles.length) summaryLines.push(`- Created: ${createdFiles.join(', ')}`);
        if (updatedFiles.length) summaryLines.push(`- Updated: ${updatedFiles.join(', ')}`);
        summaryLines.push('');

        const summaryText = summaryLines.join('\n');

        // Refresh file list to find CHANGES.md (case-insensitive)
        const refreshedFiles = await getFilesList({ sessionId });
        const filesNow = refreshedFiles.files || [];
        const changesFile = filesNow.find(
          (f) => String(f.fileName || '').toLowerCase() === 'changes.md',
        );
        if (changesFile) {
          // Prepend to existing CHANGES.md (newest changes at top)
          const existingContent = String(changesFile.content || '').trim();
          const header = existingContent.startsWith('# ') ? '' : '# Changelog\n\n';
          const newContent = header + summaryText + '\n' + existingContent;
          await updateFileEndpoint({ id: changesFile.id, content: newContent });
        } else {
          // Create CHANGES.md at root
          await createFileEndpoint({
            sessionId,
            fileName: 'CHANGES.md',
            filePath: '/',
            content: `# Changelog\n\n${summaryText}`,
          });
        }

        // Invalidate files list so CHANGES.md is visible
        queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
      } catch (e) {
        console.error('[applySuggestion] Failed updating CHANGES.md:', e);
      }

      return {
        successCount,
        failCount,
        createdFiles,
        updatedFiles,
      };
    },
    [queryClient, sessionId],
  );

  const sendMessage = useCallback(
    async (
      message: string,
      fileContext: string[] = [],
      modelSelection?: ModelSelectionOptions & { mode?: 'generate' | 'chat' },
      attachment?: { content: string; name: string; addToProject?: boolean },
      webSearchResults?: Array<{
        title: string;
        description: string;
        url: string;
        favicon: string;
        image?: string;
        position: number;
      }>,
      skills?: Array<{ name: string; content?: string; description?: string }>,
    ) => {
      // Allow unauthenticated users — operator/overseer doesn't need to be logged in
      const currentUser = authState.type === 'authenticated'
        ? authState.user
        : { id: 0, displayName: 'Operator', email: 'operator@tasking.tech', avatarUrl: null };

      // If already streaming, queue this message to send after current stream finishes
      if (streamingMessage || abortControllerRef.current) {
        queuedMessagesRef.current.push({ message, fileContext, modelSelection, attachment, webSearchResults, skills });
        // Add optimistic user message to history so it shows in the UI
        const userMsg: ChatMessage = {
          id: `temp-${Date.now()}`,
          content: stripSkillInjection(message),
          role: 'user',
          createdAt: new Date(),
          fileContext,
          userId: currentUser.id,
          userDisplayName: currentUser.displayName,
          userAvatarUrl: currentUser.avatarUrl,
          checkpointId: null,
        };
        queryClient.setQueryData(['ai', 'history', sessionId], (old: any) => ({
          ...old,
          messages: [...(old?.messages || []), userMsg],
        }));
        return;
      }

      // Get the template from localStorage (set when session was created)
      let template: string | undefined;
      try {
        const storedTemplate = localStorage.getItem(`session_template_${sessionId}`);
        if (storedTemplate) {
          template = storedTemplate;
        }
      } catch (e) {
        // localStorage access failed, ignore
      }

      // Create optimistic user message (strip skill injection from display)
      const userMsg: ChatMessage = {
        id: `temp-${Date.now()}`,
        content: stripSkillInjection(message),
        role: 'user',
        createdAt: new Date(),
        fileContext,
        userId: currentUser.id,
        userDisplayName: currentUser.displayName,
        userAvatarUrl: currentUser.avatarUrl,
        checkpointId: null,
        imageAttachment: attachment || null,
      };

      // Optimistically update history
      queryClient.setQueryData(
        ['ai', 'history', sessionId],
        (old: { messages: ChatMessage[] } | undefined) => {
          return { messages: [...(old?.messages || []), userMsg] };
        },
      );

      // Prepare streaming assistant message placeholder
      const assistantMsgId = `stream-${Date.now()}`;
      setStreamingMessage({
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        createdAt: new Date(),
      });

      try {
        abortControllerRef.current = new AbortController();

        // Format web search results as context if provided
        let enrichedMessage = message;
        if (webSearchResults && webSearchResults.length > 0) {
          const searchContext = webSearchResults
            .map(
              (result, i) =>
                `[${i + 1}] ${result.title}\n   URL: ${result.url}\n   ${result.description}`,
            )
            .join('\n\n');
          enrichedMessage = `${message}\n\n---\n**Web Search Results (Real-time):**\n${searchContext}\n---\n\nPlease use these search results to provide up-to-date information in your response.`;
        }

        const response = await postChat(
          {
            sessionId,
            message: enrichedMessage,
            fileContext,
            imageAttachment: attachment,
            useLocalLlm: modelSelection?.useLocalLlm,
            provider: modelSelection?.provider,
            model: modelSelection?.model,
            endpoint: modelSelection?.endpoint,
            apiKey: modelSelection?.apiKey,
            mode: modelSelection?.mode || mode, // Use mode from modelSelection or fallback to hook param
            template: template as any, // Template from session creation - helps AI know what framework to use
            autoLoop: true, // Enabled - AI executes steps automatically after planning
            backgroundMode: false, // Direct streaming with DB persistence (continues if client disconnects)
            skills: skills && skills.length > 0 ? skills : undefined, // Active skills — injected into system prompt
          },
          {
            signal: abortControllerRef.current.signal,
          },
        );

        // Extract active personas from response header
        const personasHeader = response.headers.get('X-Active-Personas');
        if (personasHeader) {
          try {
            const personas = JSON.parse(personasHeader);
            setActivePersonas(personas);
          } catch (e) {
            console.warn('[useAiChat] Failed to parse active personas header:', e);
          }
        }

        // Check if this is a JSON response (non-streaming)
        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
          const text = await response.text();
          try {
            // Use superjson.parse since backend uses superjson.stringify
            const data = superjson.parse(text) as any;

            // Check if it's a queued/background response
            if (data.queued && data.queueId) {
              // Request was queued for background processing
              console.log('[useAiChat] Request queued for background processing:', data.queueId);
              setStreamingMessage((prev) =>
                prev
                  ? {
                      ...prev,
                      content:
                        '⏳ Request queued for background processing...\n\n**Status:** Starting...',
                      isStreaming: true,
                      queueId: data.queueId,
                    }
                  : {
                      id: assistantMsgId,
                      role: 'assistant',
                      content:
                        '⏳ Request queued for background processing...\n\n**Status:** Starting...',
                      isStreaming: true,
                      queueId: data.queueId,
                      createdAt: new Date(),
                    },
              );

              // Start polling for results with progress updates
              const pollForResults = async () => {
                const maxPolls = 300; // Poll for up to 5 minutes (300 * 1s)
                let polls = 0;

                const poll = async () => {
                  if (polls >= maxPolls) {
                    console.log('[useAiChat] Polling timeout');
                    setStreamingMessage((prev) =>
                      prev
                        ? {
                            ...prev,
                            content:
                              '⏰ Processing timed out. Please check back later or try again.',
                            isStreaming: false,
                          }
                        : null,
                    );
                    return;
                  }
                  polls++;

                  try {
                    const statusRes = await fetch(
                      `/_api/ai-chat-queue/status?queueId=${data.queueId}`,
                    );
                    const statusData = await statusRes.json();
                    const item = statusData.item;

                    if (item?.status === 'completed') {
                      console.log('[useAiChat] Background processing completed');
                      queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
                      setStreamingMessage(null);
                      return;
                    } else if (item?.status === 'failed') {
                      console.log('[useAiChat] Background processing failed:', item.errorMessage);
                      setStreamingMessage((prev) =>
                        prev
                          ? {
                              ...prev,
                              content: `❌ Background processing failed: ${item.errorMessage || 'Unknown error'}`,
                              isStreaming: false,
                            }
                          : null,
                      );
                      queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
                      return;
                    }

                    // Update progress message
                    const currentStep = item?.currentStep || 0;
                    const totalSteps = item?.totalSteps || 0;
                    const status = item?.status || 'pending';
                    const progressMessage = item?.progressMessage || '';

                    let progressText = progressMessage;
                    let progressBar = '';
                    let stepInfo = '';

                    if (status === 'processing') {
                      if (totalSteps > 0 && currentStep > 0) {
                        const percent = Math.round((currentStep / totalSteps) * 100);
                        const filled = Math.round(percent / 5); // 20 chars total
                        const empty = 20 - filled;
                        progressBar = `\n\n\`[${'█'.repeat(filled)}${'░'.repeat(empty)}]\` **${percent}%**`;
                        stepInfo = `\n\n📊 Progress: Step ${currentStep} of ${totalSteps}`;
                        if (!progressText) {
                          progressText = `Step ${currentStep}/${totalSteps}`;
                        }
                      } else {
                        if (!progressText) {
                          progressText = 'Starting...';
                        }
                        // Show animated dots based on poll count
                        const dots = '.'.repeat((polls % 3) + 1);
                        progressBar = `\n\n⏳ Working${dots}`;
                      }
                    } else if (status === 'pending') {
                      if (!progressText) {
                        progressText = 'Waiting in queue...';
                      }
                      const dots = '.'.repeat((polls % 3) + 1);
                      progressBar = `\n\n⏳ Please wait${dots}`;
                    }

                    setStreamingMessage((prev) =>
                      prev
                        ? {
                            ...prev,
                            content: `⏳ **Background Processing**\n\n**Current:** ${progressText}${stepInfo}${progressBar}`,
                            isStreaming: true,
                          }
                        : null,
                    );

                    // Still processing, continue polling
                    setTimeout(poll, 1000);
                  } catch (e) {
                    console.warn('[useAiChat] Poll error:', e);
                    setTimeout(poll, 2000);
                  }
                };

                poll();
              };

              pollForResults();
              return;
            }

            // Check if it's an error response
            if (data.error) {
              setStreamingMessage((prev) =>
                prev
                  ? { ...prev, content: `Error: ${data.error}`, isStreaming: false }
                  : {
                      id: assistantMsgId,
                      role: 'assistant',
                      content: `Error: ${data.error}`,
                      isStreaming: false,
                      createdAt: new Date(),
                    },
              );
              return;
            }

            // Valid JSON but not a known response type - treat as complete response
            // (Stream already consumed by response.text() above, so we can't read it again)
            setStreamingMessage((prev) =>
              prev
                ? { ...prev, content: text, isStreaming: false }
                : {
                    id: assistantMsgId,
                    role: 'assistant',
                    content: text,
                    isStreaming: false,
                    createdAt: new Date(),
                  },
            );
            queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
            return;
          } catch (e) {
            // Not valid JSON, treat as text response
            setStreamingMessage((prev) =>
              prev
                ? { ...prev, content: text, isStreaming: false }
                : {
                    id: assistantMsgId,
                    role: 'assistant',
                    content: text,
                    isStreaming: false,
                    createdAt: new Date(),
                  },
            );
            return;
          }
        }

        // If the backend returned no streaming body (non-streaming response),
        // fall back to reading the full response text so attachments and sync
        // responses are handled correctly.
        if (!response.body) {
          const text = await response.text();

          setStreamingMessage((prev) =>
            prev
              ? { ...prev, content: text, isStreaming: false }
              : {
                  id: assistantMsgId,
                  role: 'assistant',
                  content: text,
                  isStreaming: false,
                  createdAt: new Date(),
                },
          );

          setLastGeneratedCode(text);
          if (autoExecute && text) {
            await applySuggestion(text);
          }
          queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';
        let lastDataAt = Date.now();
        let lastUpdateTime = Date.now();
        const streamTimeout = 300000; // 5 minute timeout — PDF generation and tool calls can take minutes
        const minUpdateInterval = 50; // Throttle updates to every 50ms minimum

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          lastDataAt = Date.now();
          const chunk = decoder.decode(value, { stream: true });
          accumulatedContent += chunk;

          // Stream timeout fires only when no data has arrived for streamTimeout ms
          if (Date.now() - lastDataAt > streamTimeout) {
            reader.cancel('Stream timeout');
            throw new Error(`AI response stream timed out after ${Math.round(streamTimeout / 1000 / 60)} minutes`);
          }

          // Throttle React state updates (max 20fps)
          const now = Date.now();
          if (now - lastUpdateTime >= minUpdateInterval) {
            lastUpdateTime = now;
            setStreamingMessage((prev) =>
              prev
                ? { ...prev, content: accumulatedContent }
                : {
                    id: assistantMsgId,
                    role: 'assistant',
                    content: accumulatedContent,
                    isStreaming: true,
                    createdAt: new Date(),
                  },
            );
          }
        }

        // Extract model tag from stream content
        let streamModel: string | null = null;
        const modelMatch = accumulatedContent.match(/\[MODEL\](.*?)\[\/MODEL\]/);
        if (modelMatch) {
          streamModel = modelMatch[1];
          accumulatedContent = accumulatedContent.replace(/\[MODEL\].*?\[\/MODEL\]/g, '');
        }

        // Final update to ensure all content is shown (with model tag)
        setStreamingMessage((prev) =>
          prev
            ? { ...prev, content: accumulatedContent, model: streamModel }
            : {
                id: assistantMsgId,
                role: 'assistant',
                content: accumulatedContent,
                isStreaming: true,
                createdAt: new Date(),
                model: streamModel,
              },
        );

        // Stream finished
        setStreamingMessage(null);
        setLastGeneratedCode(accumulatedContent);

        // Process queued messages (sent while this stream was active)
        if (queuedMessagesRef.current.length > 0) {
          const next = queuedMessagesRef.current.shift()!;
          setTimeout(() => {
            sendMessageRef.current?.(next.message, next.fileContext, next.modelSelection, next.attachment, next.webSearchResults, next.skills);
          }, 200);
        }

        // Auto-execute: automatically create files from CODE section
        // Chat mode still auto-executes if the AI generated code blocks
        const effectiveMode = modelSelection?.mode || mode;
        const hasCodeBlocks = /\[CODE\]|\[FILE:/i.test(accumulatedContent || '');
        const shouldAutoExecute = autoExecute && (effectiveMode !== 'chat' || hasCodeBlocks);

        console.log(
          '[useAiChat] Stream finished. Mode:',
          effectiveMode,
          'AutoExecute:',
          autoExecute,
          'ShouldAutoExecute:',
          shouldAutoExecute,
        );

        let autoApplyResult: ApplySuggestionResult | null = null;

        if (shouldAutoExecute && accumulatedContent) {
          console.log('[useAiChat] Auto-executing with content length:', accumulatedContent.length);

          // Parse the message to extract steps and code
          const { plan } = parseStreamingPlan(accumulatedContent);
          console.log(
            '[useAiChat] Parsed plan - Steps:',
            plan.steps?.length || 0,
            'Code length:',
            plan.code?.length || 0,
          );

          // Steps are displayed in the chat UI — no auto-todo creation
          // (todos/queues are for user-created notes, not auto-generated from AI steps)

          // Extract files from full content
          const filesToCreate = extractFilesFromMarkdown(accumulatedContent);
          console.log(
            '[useAiChat] Files to create:',
            filesToCreate.map((f) => f.filename),
          );

          if (filesToCreate.length === 0) {
            // AI returned text/steps but no code files — auto-retry once with a nudge
            const hasSteps = (plan.steps?.length || 0) > 0;
            const hasSubstantialContent = accumulatedContent.trim().length > 200;
            if ((hasSteps || hasSubstantialContent) && !emptyCodeRetryRef.current) {
              console.log('[useAiChat] AI returned steps but no code — auto-retrying with nudge');
              emptyCodeRetryRef.current = true;
              toast.info('AI planned but didn\'t generate code — retrying...', { id: 'auto-retry' });
              // Fire-and-forget retry: send a nudge message asking for actual code
              setTimeout(() => {
                const retrySend = sendMessageRef.current;
                if (!retrySend) return;
                retrySend(
                  'Now output the complete code for all files. Use ```tsx filename=path/to/file.tsx code blocks for every file. Do NOT describe steps — just output ALL the code files.',
                  [],
                  modelSelection,
                ).catch((err: any) => {
                  console.error('[useAiChat] Auto-retry failed:', err);
                  toast.error('Auto-retry failed. Please try sending your message again.');
                });
              }, 500);
            } else if ((hasSteps || hasSubstantialContent) && emptyCodeRetryRef.current) {
              console.warn('[useAiChat] AI returned steps but no code on retry — giving up');
              emptyCodeRetryRef.current = false;
              toast.error('AI generated a plan but no code files. Try rephrasing your request or switching models.', { duration: 8000 });
            }
            // else: no steps and no code — normal chat/thinking response, nothing to do
          } else {
            // Execute code - create files
            emptyCodeRetryRef.current = false; // Reset retry guard on success
            try {
              const applyResult = await applySuggestion(accumulatedContent);
              autoApplyResult = applyResult;
              // Only show toasts for failures — success is silent (chime is enough)
              if (applyResult.successCount > 0 && applyResult.failCount > 0) {
                toast.warning(
                  `${applyResult.failCount} file(s) failed to apply.`,
                  { id: 'auto-execute' },
                );
              } else if (!applyResult.skipped && applyResult.successCount === 0 && applyResult.failCount > 0) {
                toast.error('No files were created. Check errors above.', { id: 'auto-execute' });
              }
            } catch (applyErr: any) {
              console.error('[useAiChat] Error in applySuggestion:', applyErr);
              toast.error(`Failed to apply code: ${applyErr?.message || String(applyErr)}`, {
                id: 'auto-execute',
              });
            }
          }
        } else if (effectiveMode === 'chat') {
          console.log('[useAiChat] Chat mode - skipping file creation (conversation only)');
        } else if (!autoExecute) {
          console.log('[useAiChat] Auto-execute is disabled, skipping file creation');
        }

        // Play completion chime (toast notification removed — sound is sufficient)
        try {
          const muteChime = localStorage.getItem('tasking_mute_completion') === 'true';
          if (!muteChime) {
            const chime = new Audio('/chimes/completed.mp3');
            chime.volume = 0.5;
            chime.play().catch(() => {});
          }
        } catch (_) {}

        // Invalidate history to get the real persisted messages
        queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
      } catch (error: any) {
        console.error('[useAiChat] Streaming error:', error);

        // Distinguish user-initiated aborts from network/server errors.
        if (error && (error.name === 'AbortError' || error.code === 'ERR_ABORTED')) {
          // User intentionally stopped generation — clear streaming placeholder and don't append an error.
          abortControllerRef.current = null;
          setStreamingMessage(null);
          queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
          return;
        }

        // Build a more informative error message
        // First, use the actual error message from the server if available
        let errorMessage = error?.message || 'Connection interrupted';

        // Clean up common error patterns for better display
        if (errorMessage.includes('No API key configured')) {
          errorMessage = 'No API key configured. Please add your API key in AI Settings.';
        } else if (errorMessage.includes('API key') || errorMessage.includes('api key')) {
          errorMessage = 'API key issue. Please check your API key in AI Settings.';
        } else if (
          errorMessage.includes('401') ||
          errorMessage.includes('Unauthorized') ||
          errorMessage.includes('Invalid API key')
        ) {
          errorMessage = 'Invalid API key. Please check your API key in AI Settings.';
        } else if (
          errorMessage.includes('429') ||
          errorMessage.includes('rate limit') ||
          errorMessage.includes('Rate limit')
        ) {
          errorMessage = 'Rate limit exceeded. Please try again in a moment.';
        } else if (errorMessage.includes('timeout') || errorMessage.includes('ETIMEDOUT')) {
          errorMessage = 'Request timed out. The AI service may be slow or unavailable.';
        } else if (
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Network')
        ) {
          errorMessage = 'Network error. Please check your internet connection.';
        } else if (errorMessage.includes('Could not connect')) {
          errorMessage = 'Could not connect to AI provider. Please try again.';
        } else if (errorMessage.includes('empty response')) {
          errorMessage = 'AI provider returned empty response. Please try again.';
        }

        console.log('[useAiChat] Showing error to user:', errorMessage);

        // Show toast with the error
        toast.error(errorMessage);

        // For other errors, append a visible error message. If there's no existing
        // streaming placeholder, create one so the UI shows the failure text.
        setStreamingMessage((prev) =>
          prev
            ? {
                ...prev,
                content: prev.content + `\n\n[Error: ${errorMessage}]`,
                isStreaming: false,
              }
            : {
                id: assistantMsgId,
                role: 'assistant',
                content: `[Error: ${errorMessage}]`,
                isStreaming: false,
                createdAt: new Date(),
              },
        );

        // Invalidate history to sync with server state
        queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });

        // Process queued messages even after errors
        if (queuedMessagesRef.current.length > 0) {
          const next = queuedMessagesRef.current.shift()!;
          setTimeout(() => {
            sendMessageRef.current?.(next.message, next.fileContext, next.modelSelection, next.attachment, next.webSearchResults, next.skills);
          }, 500);
        }
      }
    },
    [sessionId, queryClient, authState, autoExecute, applySuggestion, mode, onCreateTask, autoLoop, streamingMessage],
  );

  // Keep ref in sync so retry closure can access latest sendMessage
  sendMessageRef.current = sendMessage;

  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setStreamingMessage(null);
      queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
    }
  }, [queryClient, sessionId]);

  return {
    sendMessage,
    streamingMessage,
    stopGeneration,
    applySuggestion,
    lastGeneratedCode,
    activePersonas,
  };
};
